import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Table, Card } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ButtonLoader from "../../../components/Common/ButtonLoader";
import {
  GET_BASIC_SETTING,
  UPDATE_BASIC_SETTING,
} from "../../../services/ApiCalls";

const UserNotification = ({ data }) => {
  const [loader, setLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [dataSetting, setDataSetting] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await GET_BASIC_SETTING();
      if (res?.data?.status === "success") {
        const settings = res.data.data[0];
        setDataSetting(settings);
      }
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const userNotifications = dataSetting?.userNotification || [];
  const hotelAdminNotifications = dataSetting?.hotelAdminNotification || [];

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userNotifications: {},
      hotelAdminNotifications: {},
    },
  });

  // Update form values when dataSetting loads
  useEffect(() => {
    if (dataSetting && userNotifications.length > 0) {
      const userDefaultValues = {};
      userNotifications.forEach((notif) => {
        const key = notif._id || notif.id;
        userDefaultValues[`user_${key}_push`] = notif.push || false;
        userDefaultValues[`user_${key}_inApp`] = notif.inApp || false;
        userDefaultValues[`user_${key}_email`] = notif.email || false;
      });

      const hotelDefaultValues = {};
      hotelAdminNotifications.forEach((notif) => {
        const key = notif._id || notif.id;
        hotelDefaultValues[`hotel_${key}_push`] = notif.push || false;
        hotelDefaultValues[`hotel_${key}_inApp`] = notif.inApp || false;
        hotelDefaultValues[`hotel_${key}_email`] = notif.email || false;
      });

      reset({
        ...userDefaultValues,
        ...hotelDefaultValues,
      });
    }
  }, [dataSetting, reset]);

  const onSubmit = async (formData) => {
    setLoader(true);
    try {
      // Prepare the data for API - update the notification arrays
      const updatedUserNotifications = userNotifications.map((notif) => {
        const key = notif._id || notif.id;
        return {
          ...notif,
          push: formData[`user_${key}_push`] || false,
          inApp: formData[`user_${key}_inApp`] || false,
          email: formData[`user_${key}_email`] || false,
        };
      });

      const updatedHotelNotifications = hotelAdminNotifications.map((notif) => {
        const key = notif._id || notif.id;
        return {
          ...notif,
          push: formData[`hotel_${key}_push`] || false,
          inApp: formData[`hotel_${key}_inApp`] || false,
          email: formData[`hotel_${key}_email`] || false,
        };
      });

      const updateData = {
        ...dataSetting,
        userNotification: updatedUserNotifications,
        hotelAdminNotification: updatedHotelNotifications,
      };

      const res = await UPDATE_BASIC_SETTING(updateData);
      if (res?.data?.status === "success") {
        toast.success("Notification settings updated successfully!");
        // Update local state with new data
        setDataSetting(updateData);
      } else {
        toast.error(res?.data?.message || "Failed to update settings");
      }
    } catch (err) {
      console.error("Update error:", err);
      toast.error("Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const handleToggleAll = (section, type, value) => {
    if (section === "user") {
      userNotifications.forEach((notif) => {
        const key = notif._id || notif.id;
        setValue(`user_${key}_${type}`, value);
      });
    } else {
      hotelAdminNotifications.forEach((notif) => {
        const key = notif._id || notif.id;
        setValue(`hotel_${key}_${type}`, value);
      });
    }
  };

  const CheckboxCell = ({ name, control }) => (
    <td className="text-center">
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Form.Check
            type="checkbox"
            checked={field.value || false}
            onChange={(e) => field.onChange(e.target.checked)}
            className="d-inline-flex align-items-center justify-content-center"
          />
        )}
      />
    </td>
  );

  if (loading) {
    return (
      <Col lg="12" className="my-2">
        <div className="cardCstm p-3 rounded bg-white py-4 text-center">
          {/* <ButtonLoader /> */}
          <div>Loading notification settings...</div>
        </div>
      </Col>
    );
  }

  return (
    <Col lg="12" className="my-2">
      <div className="cardCstm p-3 rounded bg-white py-4 user-notofication">
        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* User Notifications Section */}
          <div className="mb-5">
            <h4 className="fw-bold text-dark mb-4">User Notifications</h4>
            <Card className="border-0 shadow-sm checkcards">
              <Card.Body className="p-0">
                <Table responsive className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="fw-semibold text-muted py-3 ps-4">
                        Event
                      </th>
                      <th className="text-center fw-semibold text-muted py-3">
                        <div className="d-flex flex-column align-items-center">
                          <span>Push</span>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-primary"
                            onClick={() =>
                              handleToggleAll("user", "push", true)
                            }
                          >
                            All
                          </Button>
                        </div>
                      </th>
                      <th className="text-center fw-semibold text-muted py-3">
                        <div className="d-flex flex-column align-items-center">
                          <span>In-App</span>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-primary"
                            onClick={() =>
                              handleToggleAll("user", "inApp", true)
                            }
                          >
                            All
                          </Button>
                        </div>
                      </th>
                      <th className="text-center fw-semibold text-muted py-3">
                        <div className="d-flex flex-column align-items-center">
                          <span>Email</span>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-primary"
                            onClick={() =>
                              handleToggleAll("user", "email", true)
                            }
                          >
                            All
                          </Button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {userNotifications.map((notification) => {
                      const key = notification._id || notification.id;
                      return (
                        <tr key={key} className="border-bottom ">
                          <td className="ps-4 py-3 fw-medium">
                            {notification.eventType}
                          </td>
                          <CheckboxCell
                          
                            name={`user_${key}_push`}
                            control={control}
                          />
                          <CheckboxCell
                            name={`user_${key}_inApp`}
                            control={control}
                          />
                          <CheckboxCell
                            name={`user_${key}_email`}
                            control={control}
                          />
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </div>

          {/* Divider */}
          <hr className="my-4" />

          {/* Hotel Admin Notifications Section */}
          <div className="mb-4">
            <h4 className="fw-bold text-dark mb-4">Hotel Admin Notification</h4>
            <Card className="border-0 shadow-sm checkcards">
              <Card.Body className="p-0">
                <Table responsive className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="fw-semibold text-muted py-3 ps-4">
                        Event
                      </th>
                      <th className="text-center fw-semibold text-muted py-3">
                        <div className="d-flex flex-column align-items-center">
                          <span>Push</span>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-primary"
                            onClick={() =>
                              handleToggleAll("hotel", "push", true)
                            }
                          >
                            All
                          </Button>
                        </div>
                      </th>
                      <th className="text-center fw-semibold text-muted py-3">
                        <div className="d-flex flex-column align-items-center">
                          <span>In-App</span>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-primary"
                            onClick={() =>
                              handleToggleAll("hotel", "inApp", true)
                            }
                          >
                            All
                          </Button>
                        </div>
                      </th>
                      <th className="text-center fw-semibold text-muted py-3">
                        <div className="d-flex flex-column align-items-center">
                          <span>Email</span>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-primary"
                            onClick={() =>
                              handleToggleAll("hotel", "email", true)
                            }
                          >
                            All
                          </Button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {hotelAdminNotifications.map((notification) => {
                      const key = notification._id || notification.id;
                      return (
                        <tr key={key} className="border-bottom">
                          <td className="ps-4 py-3 fw-medium">
                            {notification.eventType}
                          </td>
                          <CheckboxCell
                            name={`hotel_${key}_push`}
                            control={control}
                          />
                          <CheckboxCell
                            name={`hotel_${key}_inApp`}
                            control={control}
                          />
                          <CheckboxCell
                            name={`hotel_${key}_email`}
                            control={control}
                          />
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </div>
          
          <div className="d-flex justify-content-end mt-4">
            <Button
              type="submit"
              className="fw-semibold commonBtn primaryBtn"
              disabled={loader}
              style={{ minWidth: "120px" }}
            >
              {loader ? <ButtonLoader /> : "Save Settings"}
            </Button>
          </div>
        </Form>
      </div>
    </Col>
  );
};

export default UserNotification;
